import { useState, type FormEvent } from 'react';
import { useNurseryIntake } from '../nursery/NurseryIntake';
import { Link, useLocation } from 'react-router';
import { useTabParam } from '../../shared/ui/useTabParam';
import { api, ApiError, errorMessage } from '../../shared/api/client';
import { missingData, type ChurchFamily, type ChurchMember, type Membership, type Person } from '../../shared/api/types';
import { Alert, Button, Card, EmptyState, ErrorState, Field, Loading } from '../../shared/ui/components';
import { LegacyBadges } from '../../shared/ui/LegacyBadges';
import { useAsync } from '../../shared/ui/useAsync';
import { MembershipRequests } from '../churches/MembershipRequestsPage';
import { useMe } from '../session/SessionProvider';
import { formatDay, fullName, roleLabel } from './roles';
import { PersonDetailsFields, readPersonDetails } from '../../shared/ui/PersonDetailsFields';
import { BulkRegisterCard } from './BulkRegister';
import { FamiliesTab, FamilyChangeRequests } from './FamiliesTab';
import { VisitorsPage } from '../visitors/VisitorsPage';
import { useVisitorAccess } from '../visitors/shared';
import { QrCode } from '../nursery/QrCode';
import { joinPath } from '../churches/JoinByInvitePage';
import { showFieldErrors } from '../../shared/ui/fieldErrors';
import { Avatar } from '../../shared/ui/Avatar';

type Tab = 'members' | 'families' | 'requests' | 'visits';

/** Tarjeta para invitar: QR directo para registrarse en la Iglesia y el enlace (se manda por WhatsApp). */
function InviteCard({ churchId, onClose }: { churchId: string; onClose: () => void }) {
  const [copied, setCopied] = useState(false);
  const link = `${window.location.origin}${joinPath(churchId)}`;
  async function copy() {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  }
  const whatsapp = `https://wa.me/?text=${encodeURIComponent(`Te invito a unirte a nuestra iglesia en KEORA: ${link}`)}`;
  return (
    <Card
      title="QR para registrarse en la iglesia"
      action={
        <Button variant="ghost" onClick={onClose}>
          Cerrar
        </Button>
      }
    >
      <p className="muted">Quien lo escanee crea su cuenta (o inicia sesión) y su solicitud llega directo a la pestaña «Por aprobar».</p>
      <div className="qr-print">
        <QrCode value={link} label="Código QR para registrarse en la iglesia" />
        <p className="center">Escanea para unirte a nuestra iglesia.</p>
      </div>
      <div className="code-row">
        <code className="code">{link}</code>
      </div>
      <div className="actions actions--wrap">
        <Button variant="secondary" onClick={() => void copy()}>
          {copied ? 'Enlace copiado' : 'Copiar enlace'}
        </Button>
        <a className="btn btn--secondary" href={whatsapp} target="_blank" rel="noreferrer">
          Enviar por WhatsApp
        </a>
        <Button variant="secondary" onClick={() => window.print()}>
          Imprimir QR
        </Button>
      </div>
    </Card>
  );
}

/** Datos de una visita para registrarla como miembro. */
export interface FromVisit {
  visitorId: string;
  person: Partial<Person>;
  email: string | null;
}

type SimilarPerson = { personId: string; name: string; birthDate: string | null; family: string | null; membership: string | null };

type NurseryData = Awaited<ReturnType<ReturnType<typeof useNurseryIntake>['read']>>;

/** Formulario para que el administrador registre directamente a un miembro. */
function RegisterMemberCard({ churchId, onDone, onCancel, fromVisit }: { churchId: string; onDone: (message: string) => void; onCancel: () => void; fromVisit?: FromVisit | null }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // «¿Es esta persona?»: KEORA encontró a alguien parecido; se registra solo si es otra persona.
  const [similar, setSimilar] = useState<{ people: SimilarPerson[]; details: ReturnType<typeof readPersonDetails>['details']; email: string; nurseryData: NurseryData } | null>(null);
  // Un menor de 3 años: se pregunta si usará la guardería y se piden sus datos.
  const [birthDate, setBirthDate] = useState<string | null>(fromVisit?.person?.birthDate ?? null);
  const nursery = useNurseryIntake('new-member', birthDate);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const formEl = event.currentTarget;
    const form = new FormData(formEl);
    // Basta nombre, apellido y género (p. ej. para asignarle una promesa); lo demás se completa en su ficha.
    const { details, error: missing, fields } = readPersonDetails(form, { requireAll: false, requireGender: true });
    const email = String(form.get('email') ?? '').trim();
    if (missing) {
      setError(missing);
      showFieldErrors(formEl, fields);
      return;
    }
    const nurseryMissing = nursery.missing();
    if (nurseryMissing) {
      setError(nurseryMissing);
      return;
    }
    await register(details, email, false, await nursery.read(form));
  }

  async function register(details: ReturnType<typeof readPersonDetails>['details'], email: string, force: boolean, nurseryData: NurseryData) {
    setBusy(true);
    setError(null);
    try {
      const res = await api.post<{ invited: boolean }>(`/multichurch/churches/${churchId}/members`, { ...details, email, force, nursery: nurseryData });
      // La visita se convierte en miembro: su etapa pasa a «Miembro».
      if (fromVisit) await api.put(`/multichurch/churches/${churchId}/visitors/${fromVisit.visitorId}/stage`, { stage: 'miembro' }).catch(() => undefined);
      const name = [details.givenName, details.paternalSurname, details.maternalSurname].filter(Boolean).join(' ');
      onDone(res.invited ? `${name} ya es miembro. Le enviamos un correo a ${email} para que cree su contraseña.` : `${name} ya es miembro.`);
    } catch (e) {
      const people = e instanceof ApiError && e.status === 409 ? (e.details?.similar as SimilarPerson[] | undefined) : undefined;
      if (people?.length) setSimilar({ people, details, email, nurseryData });
      else setError(errorMessage(e));
      setBusy(false);
    }
  }

  if (similar) {
    return (
      <Card title="¿Es esta persona?">
        <p className="muted">Ya hay alguien parecido en tu iglesia. Si es la misma persona, no la registres otra vez.</p>
        <ul className="list">
          {similar.people.map((p) => (
            <li key={p.personId}>
              <strong>{p.name}</strong>
              {p.birthDate ? ` · nació ${formatDay(p.birthDate)}` : ''}
              {p.family ? ` · familia ${p.family}` : ''}
              {` · ${p.membership === 'active' ? 'miembro' : p.membership === 'pending' ? 'solicitud pendiente' : 'sin membresía'}`}
            </li>
          ))}
        </ul>
        <div className="actions actions--wrap">
          <Button type="button" onClick={() => onDone('No se registró: esa persona ya está en tu iglesia. Búscala en el directorio o en Familias.')}>
            Sí, es esa persona
          </Button>
          <Button type="button" variant="secondary" busy={busy} onClick={() => void register(similar.details, similar.email, true, similar.nurseryData)}>
            No, es otra: registrarla
          </Button>
        </div>
      </Card>
    );
  }

  return (
    <Card title="Registrar miembro">
      <form className="form" onSubmit={submit} onChange={(e) => setBirthDate(readPersonDetails(new FormData(e.currentTarget)).details.birthDate)} noValidate>
        {error ? <Alert>{error}</Alert> : null}
        {fromVisit ? <Alert tone="info">Usamos los datos que dejó en su visita. Completa lo que falte.</Alert> : null}
        <p className="muted small">Solo son obligatorios el nombre, el apellido paterno y el género. Lo demás lo puedes completar después en su ficha.</p>
        <PersonDetailsFields idPrefix="new-member" person={fromVisit?.person} quick />
        {nursery.fields}
        <Field
          label="Correo (opcional)"
          name="email"
          type="email"
          autoComplete="off"
          defaultValue={fromVisit?.email ?? ''}
          hint="Si lo escribes, le llegará un correo para crear su contraseña y entrar a KEORA. Déjalo vacío para quien no usará la app."
        />
        <div className="actions">
          <Button type="submit" busy={busy}>
            Registrar
          </Button>
          <Button type="button" variant="ghost" onClick={onCancel}>
            Cancelar
          </Button>
        </div>
      </form>
    </Card>
  );
}

/** Miembros de la Iglesia activa: directorio, solicitudes e invitación. */
export function MembersPage() {
  const me = useMe();
  const churchId = me.activeChurchId;
  const canApprove = me.can('members.approve');
  const canRead = me.can('members.read');
  // Visitas: quienes atienden visitantes (Diaconado, Pastoral, administración) la ven aunque no vean el directorio.
  const visitors = useVisitorAccess(churchId);
  // La pestaña vive en la dirección (?tab=familias…): al recargar no se pierde.
  // «Por aprobar» conserva ?tab=solicitudes: así siguen sirviendo los avisos y enlaces de antes.
  const [tabKey, setTabKey] = useTabParam(['directorio', 'familias', 'solicitudes', 'visitas'] as const);
  const TAB_OF = { directorio: 'members', familias: 'families', solicitudes: 'requests', visitas: 'visits' } as const;
  const tab: Tab = !canRead && visitors.canUse ? 'visits' : TAB_OF[tabKey];
  const setTab = (t: Tab) => setTabKey((Object.keys(TAB_OF) as (keyof typeof TAB_OF)[]).find((k) => TAB_OF[k] === t)!);
  const [query, setQuery] = useState('');
  const [show, setShow] = useState<'todos' | 'sin_cuenta' | 'incompletos'>('todos');
  // «Registrar como miembro» desde una visita: llega con sus datos.
  const fromVisit = (useLocation().state as { fromVisit?: FromVisit } | null)?.fromVisit ?? null;
  const [registering, setRegistering] = useState(Boolean(fromVisit));
  const [bulk, setBulk] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const canManage = me.can('members.manage');
  // «Invitar» es un botón junto al título, solo para quien aprueba solicitudes.
  const [inviting, setInviting] = useState(false);

  function startRegistering() {
    setNotice(null);
    setRegistering(true);
  }
  const members = useAsync(
    (signal) => (churchId && canRead ? api.get<{ items: ChurchMember[] }>(`/multichurch/churches/${churchId}/members`, signal).then((r) => r.items) : Promise.resolve([])),
    [churchId],
  );
  const families = useAsync(
    (signal) => (churchId && canRead ? api.get<{ items: ChurchFamily[] }>(`/multichurch/churches/${churchId}/families`, signal).then((r) => r.items) : Promise.resolve([])),
    [churchId],
  );
  const pending = useAsync(
    (signal) =>
      churchId && canApprove
        ? api.get<{ items: (Membership & { person: Person | null })[] }>(`/multichurch/churches/${churchId}/memberships?status=pending`, signal).then((r) => r.items.length)
        : Promise.resolve(0),
    // Al abrir Familias se reflejan los hijos de Mi familia y llegan sus solicitudes: se vuelve a contar.
    [churchId, canApprove, families.status],
  );
  // Los cambios que piden las familias también esperan aprobación (los ve quien administra miembros).
  const familyRequests = useAsync(
    (signal) =>
      churchId && canManage
        ? api.get<{ items: unknown[] }>(`/multichurch/churches/${churchId}/family-requests`, signal).then((r) => r.items.length)
        : Promise.resolve(0),
    [churchId, canManage],
  );

  if (!churchId) {
    return (
      <div className="page">
        <h1>Miembros</h1>
        <EmptyState title="Elige primero una iglesia en el selector de arriba." />
      </div>
    );
  }

  const familyOf = new Map<string, string[]>();
  if (families.status === 'ready') {
    for (const f of families.data) for (const m of f.members) familyOf.set(m.personId, [...(familyOf.get(m.personId) ?? []), f.name]);
  }
  const q = query.trim().toLowerCase();
  const list =
    members.status === 'ready'
      ? members.data.filter(
          (m) =>
            (!q || `${fullName(m)} ${m.email ?? ''}`.toLowerCase().includes(q)) &&
            (show === 'todos' || (show === 'sin_cuenta' ? m.hasAccount === false : missingData(m).length > 0)),
        )
      : [];
  const memberCount = members.status === 'ready' ? members.data.filter((m) => m.membershipId).length : 0;
  const pendingCount = pending.status === 'ready' ? pending.data : 0;
  const familyRequestCount = familyRequests.status === 'ready' ? familyRequests.data : 0;
  const toApprove = pendingCount + familyRequestCount;

  return (
    <div className="page">
      <header className={`page__header${canApprove ? ' page__header--actions' : ''}`}>
        <div>
          <h1>Miembros</h1>
          <p className="muted">
            {me.activeContext?.churchName}
            {members.status === 'ready' ? ` · ${memberCount} ${memberCount === 1 ? 'miembro' : 'miembros'}` : ''}
          </p>
        </div>
        {canApprove && !inviting ? (
          <Button variant="secondary" onClick={() => setInviting(true)}>
            Invitar
          </Button>
        ) : null}
      </header>
      {canApprove && inviting ? <InviteCard churchId={churchId} onClose={() => setInviting(false)} /> : null}

      <div className="tabs tabs--scroll" role="tablist" aria-label="Miembros">
        {canRead ? (
        <>
        <button type="button" role="tab" aria-selected={tab === 'members'} className={`tabs__tab ${tab === 'members' ? 'tabs__tab--active' : ''}`} onClick={() => setTab('members')}>
          Directorio
        </button>
        <button type="button" role="tab" aria-selected={tab === 'families'} className={`tabs__tab ${tab === 'families' ? 'tabs__tab--active' : ''}`} onClick={() => setTab('families')}>
          Familias
        </button>
        {canApprove || canManage ? (
          <button type="button" role="tab" aria-selected={tab === 'requests'} className={`tabs__tab ${tab === 'requests' ? 'tabs__tab--active' : ''}`} onClick={() => setTab('requests')}>
            Por aprobar{toApprove > 0 ? ` (${toApprove})` : ''}
          </button>
        ) : null}
        </>
        ) : null}
        {visitors.canUse ? (
          <button type="button" role="tab" aria-selected={tab === 'visits'} className={`tabs__tab ${tab === 'visits' ? 'tabs__tab--active' : ''}`} onClick={() => setTab('visits')}>
            Visitas
          </button>
        ) : null}
      </div>
      {tab === 'visits' ? <VisitorsPage embedded /> : null}

      {tab === 'members' ? (
        <>
          {notice ? <Alert tone="success">{notice}</Alert> : null}
          {canManage && !registering ? (
            <div>
              <div className="actions actions--wrap">
                <Button onClick={startRegistering}>Registrar miembro</Button>
                {!bulk ? (
                  <Button variant="secondary" onClick={() => setBulk(true)}>
                    Registrar varios
                  </Button>
                ) : null}
              </div>
            </div>
          ) : null}
          {bulk && churchId ? <BulkRegisterCard churchId={churchId} onDone={members.reload} onCancel={() => setBulk(false)} /> : null}
          {registering ? (
            <RegisterMemberCard
              churchId={churchId}
              fromVisit={fromVisit}
              onCancel={() => setRegistering(false)}
              onDone={(message) => {
                setRegistering(false);
                setNotice(message);
                members.reload();
              }}
            />
          ) : null}
          {toApprove > 0 ? (
            <Alert tone="info">
              {toApprove === 1 ? 'Hay 1 pendiente' : `Hay ${toApprove} pendientes`} por aprobar.{' '}
              <button type="button" className="link-button" onClick={() => setTab('requests')}>
                Revisar
              </button>
            </Alert>
          ) : null}
          <div className="field">
            <label htmlFor="member-search">Buscar</label>
            <input id="member-search" type="search" placeholder="Nombre o correo" value={query} onChange={(e) => setQuery(e.target.value)} />
          </div>
          <div className="field">
            <label htmlFor="member-show">Mostrar</label>
            <select id="member-show" value={show} onChange={(e) => setShow(e.target.value as typeof show)}>
              <option value="todos">Todos</option>
              <option value="sin_cuenta">Capturados por la iglesia (sin cuenta)</option>
              <option value="incompletos">Con datos incompletos</option>
            </select>
          </div>
          <Card>
            {members.status === 'loading' ? <Loading /> : null}
            {members.status === 'error' ? <ErrorState error={members.error} onRetry={members.reload} /> : null}
            {members.status === 'ready' && list.length === 0 ? (
              <EmptyState title={q || show !== 'todos' ? 'Nadie coincide con la búsqueda.' : 'Todavía no hay miembros.'}>
                {!q && canApprove ? (
                  <button type="button" className="link-button" onClick={() => setInviting(true)}>
                    Invitar a la iglesia
                  </button>
                ) : null}
              </EmptyState>
            ) : null}
            {list.length > 0 ? (
              <ul className="member-list">
                {list.map((m) => (
                  <li key={m.personId}>
                    <Link to={`/app/miembros/${m.personId}`} className="member-row">
                      <Avatar personId={m.personId} name={fullName(m)} />
                      <span className="member-row__main">
                        <strong>
                          {fullName(m)}
                          <LegacyBadges founder={m.founder} />
                        </strong>
                        <span className="small muted">
                          {m.email ?? 'Sin correo'}
                          {m.memberSince ? ` · desde ${formatDay(m.memberSince)}` : ''}
                          {familyOf.has(m.personId) ? ` · ${familyOf.get(m.personId)!.join(', ')}` : ''}
                        </span>
                        {m.hasAccount === false || missingData(m).length ? (
                          <span className="member-row__tags">
                            {m.hasAccount === false ? <span className="badge badge--warn">Sin cuenta</span> : null}
                            {missingData(m).length ? <span className="badge">Datos incompletos</span> : null}
                          </span>
                        ) : null}
                      </span>
                      <span className="member-row__roles">
                        {m.roles.filter((r) => r !== 'member').map((r) => (
                          <span key={r} className="badge badge--active">
                            {roleLabel(r)}
                          </span>
                        ))}
                        {!m.membershipId ? <span className="badge">Sin membresía</span> : null}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            ) : null}
          </Card>
        </>
      ) : null}

      {tab === 'families' ? (
        <FamiliesTab
          churchId={churchId}
          families={families}
          people={members.status === 'ready' ? members.data : []}
          canManage={canManage}
          canApprove={canApprove}
          onApproved={() => {
            members.reload();
            pending.reload();
          }}
        />
      ) : null}

      {tab === 'requests' && (canApprove || canManage) ? (
        <>
          <p className="muted small">Todo lo que espera tu aprobación: quienes quieren unirse, los miembros por aprobar y los cambios que piden las familias.</p>
          {pending.status === 'ready' && familyRequests.status === 'ready' && toApprove === 0 ? (
            <Card>
              <EmptyState title="No hay nada por aprobar." />
            </Card>
          ) : null}
          {canApprove ? (
            <MembershipRequests
              title="Quieren ser miembros"
              hideWhenEmpty
              onDecided={() => {
                members.reload();
                pending.reload();
              }}
            />
          ) : null}
          {canManage ? (
            <FamilyChangeRequests
              churchId={churchId}
              onDecided={() => {
                families.reload();
                familyRequests.reload();
              }}
            />
          ) : null}
        </>
      ) : null}
    </div>
  );
}
